import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, RefreshCcw, Mail, CheckCircle2, XCircle, AlertTriangle, Server, Send } from "lucide-react";
import { toast } from "sonner";
import { BlockedEmailsResend } from "@/components/BlockedEmailsResend";

/**
 * Email diagnostics dashboard — read-only.
 *
 * Answers the "why aren't emails going through on prod" question by surfacing:
 *   · Effective provider (resend vs mock) computed from live env vars
 *   · Sender + fallback addresses that the backend will use for real sends
 *   · Aggregate counts of sent / sent_mock / failed over 24h and 7d
 *   · Last 50 email_log rows so admins can inspect specific failures
 *
 * The endpoint is on /api/admin/email-diagnostics (routes_admin.py). Kept in
 * its own page so it's grep-able from the admin menu and never accidentally
 * shown to non-admins.
 */
export default function AdminEmailDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-diagnostics");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load diagnostics");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Iter93 — retry a historical failed / throttled / queued email so admins
  // can rescue messages without having to trigger the original event again.
  // The endpoint clones subject/body/html into a new email_log row and
  // resends via the current provider.
  const [retryingId, setRetryingId] = useState(null);
  const retry = async (row) => {
    setRetryingId(row.id);
    try {
      const { data: r } = await api.post(`/admin/email-log/${row.id}/retry`);
      if (r?.ok) {
        toast.success("Resent successfully");
      } else {
        toast.error(`Retry landed in state '${r?.new_status || "unknown"}'`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Retry failed");
    } finally { setRetryingId(null); }
  };

  if (loading && !data) {
    return <div className="text-xs text-[#525252] uppercase tracking-wider" data-testid="email-diag-loading">Loading email diagnostics…</div>;
  }
  if (!data) return null;

  const isMock = data.provider === "mock";

  return (
    <div className="space-y-6" data-testid="admin-email-diagnostics-page">
      <div>
        <Link to="/admin" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-2 mb-3" data-testid="back-to-admin">
          <ArrowLeft className="w-3 h-3" /> Admin overview
        </Link>
        <div className="label-mono mb-2" style={{ color: isMock ? "#FF4500" : "#CCFF00" }}>EMAIL DIAGNOSTICS</div>
        <h1 className="h-display text-5xl tracking-tighter">
          {isMock ? "Emails are NOT sending." : "Emails are wired up."}
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          {isMock
            ? "Provider is running in MOCK mode — messages are logged to the DB but never delivered. Check the env vars below on the server hosting this environment."
            : "Provider is Resend. Recent sends and failures shown below — if you see 'failed' rows with 'domain not verified', add your sending domain in the Resend dashboard."}
        </p>
      </div>

      <BlockedEmailsResend />

      {/* Provider + env */}
      <section className="card-surface p-5" data-testid="email-diag-provider">
        <div className="flex items-center gap-2 mb-4">
          <Server className="w-4 h-4 text-[#CCFF00]" />
          <div className="label-mono">EFFECTIVE PROVIDER</div>
          <span
            className="ml-auto label-mono px-2 py-0.5"
            style={{ background: isMock ? "#FF4500" : "#CCFF00", color: "#0A0A0A", fontWeight: 700 }}
            data-testid="email-diag-provider-value"
          >
            {(data.provider || "?").toUpperCase()}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <KV label="EMAIL_ENABLED" value={data.env.EMAIL_ENABLED || "— (defaults to mock)"} bad={data.env.EMAIL_ENABLED !== "resend"} />
          <KV label="RESEND_API_KEY" value={data.env.RESEND_API_KEY_present ? "set" : "MISSING"} bad={!data.env.RESEND_API_KEY_present} />
          <KV label="SENDER_EMAIL" value={data.env.SENDER_EMAIL} />
          <KV label="SENDER_EMAIL_FALLBACK" value={data.env.SENDER_EMAIL_FALLBACK} />
        </div>
      </section>

      {/* Rate limits vs Resend caps */}
      {data.rate_limits && <RateLimitsCard usage={data.rate_limits} />}

      {/* Aggregates */}
      <div className="grid md:grid-cols-2 gap-4">
        <BucketCard title="Last 24 hours" data={data.last_24h} testid="email-diag-24h" />
        <BucketCard title="Last 7 days" data={data.last_7d} testid="email-diag-7d" />
      </div>

      {/* Recent log */}
      <section className="card-surface p-5" data-testid="email-diag-recent">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="label-mono flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> RECENT 50 EMAILS</div>
          <button
            type="button"
            onClick={load}
            className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5"
            data-testid="email-diag-refresh"
          >
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {data.recent_50.length === 0 ? (
          <div className="text-sm text-[#525252] py-6 text-center">No email attempts yet on this server.</div>
        ) : (
          <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
            {data.recent_50.map((r) => {
              const badge = STATUS_BADGE[r.status] || STATUS_BADGE.unknown;
              const retryable = ["failed", "throttled", "queued"].includes((r.status || "").toLowerCase());
              return (
                <div key={r.id} className="border border-[#262626] p-3" data-testid={`email-diag-row-${r.id}`}>
                  <div className="flex items-center gap-2 mb-1.5 text-[10px] uppercase tracking-wider flex-wrap">
                    <span className="label-mono px-1.5 py-0.5" style={{ background: badge.bg, color: badge.fg, fontWeight: 700 }}>
                      {badge.icon}{r.status || "unknown"}
                    </span>
                    <span className="text-[#A3A3A3]">{r.kind}</span>
                    <span className="text-[#525252]">·</span>
                    <span className="text-[#A3A3A3]">{new Date(r.created_at).toLocaleString()}</span>
                    <span className="text-[#525252] ml-auto">from {r.sent_from || r.from}</span>
                    {retryable && (
                      <button
                        type="button"
                        onClick={() => retry(r)}
                        disabled={retryingId === r.id}
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`email-diag-retry-${r.id}`}
                      >
                        <Send className="w-3 h-3" /> {retryingId === r.id ? "Resending…" : "Retry"}
                      </button>
                    )}
                    {r.retry_email_id && (
                      <span className="label-mono text-[9px] text-[#525252]" data-testid={`email-diag-retried-${r.id}`}>
                        RETRIED
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-white truncate flex-1">{r.subject}</span>
                    <span className="text-[#A3A3A3] shrink-0">→ {r.to_name || r.to}</span>
                  </div>
                  {r.error && (
                    <div className="mt-1.5 text-[11px] text-[#EF4444] font-mono truncate" title={r.error}>
                      {r.error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {isMock && (
        <section className="card-surface p-5 border-l-4 border-[#FF4500]" data-testid="email-diag-tip">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#FF4500] mt-0.5 shrink-0" />
            <div>
              <div className="label-mono mb-1" style={{ color: "#FF4500" }}>HOW TO FIX</div>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">
                On the server hosting this environment, set:<br />
                <code className="text-[#CCFF00]">EMAIL_ENABLED=resend</code>,{" "}
                <code className="text-[#CCFF00]">RESEND_API_KEY=re_…</code>,{" "}
                <code className="text-[#CCFF00]">SENDER_EMAIL=noreply@rally-lab.com</code>,{" "}
                <code className="text-[#CCFF00]">SENDER_EMAIL_FALLBACK=onboarding@resend.dev</code>.<br />
                Restart the backend. Refresh this page — provider should flip to RESEND.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

const STATUS_BADGE = {
  sent: { bg: "#CCFF00", fg: "#0A0A0A", icon: "" },
  sent_mock: { bg: "#FF4500", fg: "#0A0A0A", icon: "" },
  failed: { bg: "#EF4444", fg: "#FFFFFF", icon: "" },
  throttled: { bg: "#F59E0B", fg: "#0A0A0A", icon: "" },
  queued: { bg: "#262626", fg: "#A3A3A3", icon: "" },
  unknown: { bg: "#262626", fg: "#A3A3A3", icon: "" },
};

function RateLimitsCard({ usage }) {
  // Colour-coded meters against the Resend free-tier caps (100/day, 3000/mo
  // by default; overridable via EMAIL_DAILY_LIMIT / EMAIL_MONTHLY_LIMIT env
  // vars). When a cap is hit, real sends are marked `throttled` in email_log.
  const dayPct = usage.day_limit > 0 ? Math.min(100, Math.round((usage.day_used / usage.day_limit) * 100)) : 0;
  const monthPct = usage.month_limit > 0 ? Math.min(100, Math.round((usage.month_used / usage.month_limit) * 100)) : 0;
  const barColor = (pct, capped) => capped ? "#EF4444" : pct >= 80 ? "#F59E0B" : "#CCFF00";
  return (
    <section className="card-surface p-5" data-testid="email-diag-rate-limits">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="label-mono">RESEND RATE LIMITS</div>
        {(usage.day_capped || usage.month_capped) && (
          <span
            className="label-mono px-1.5 py-0.5 text-[10px]"
            style={{ background: "#EF4444", color: "#FFFFFF", fontWeight: 700 }}
            data-testid="email-diag-capped-badge"
          >
            CAP HIT · sends throttled
          </span>
        )}
        <span className="text-[10px] uppercase tracking-wider text-[#525252] ml-auto">
          Change in Admin Console
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <Meter
          label="Today (UTC)"
          used={usage.day_used}
          limit={usage.day_limit}
          pct={dayPct}
          capped={usage.day_capped}
          color={barColor(dayPct, usage.day_capped)}
          testid="email-diag-day-meter"
        />
        <Meter
          label="This month (UTC)"
          used={usage.month_used}
          limit={usage.month_limit}
          pct={monthPct}
          capped={usage.month_capped}
          color={barColor(monthPct, usage.month_capped)}
          testid="email-diag-month-meter"
        />
      </div>
    </section>
  );
}

function Meter({ label, used, limit, pct, capped, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="label-mono text-[10px] text-[#A3A3A3]">{label}</div>
        <div className="text-xs font-mono" style={{ color }}>
          <span className="text-lg font-bold">{used}</span>
          <span className="text-[#525252]"> / {limit}</span>
        </div>
      </div>
      <div className="h-2 bg-[#141414] border border-[#262626]">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="text-[10px] text-[#525252] uppercase tracking-wider mt-1">
        {capped ? "Cap reached — new sends will be throttled" : `${Math.max(0, limit - used)} remaining`}
      </div>
    </div>
  );
}

function KV({ label, value, bad = false }) {
  return (
    <div>
      <div className="label-mono text-[10px] text-[#A3A3A3]">{label}</div>
      <div className="text-sm font-mono" style={{ color: bad ? "#EF4444" : "#E5E5E5" }}>{value}</div>
    </div>
  );
}

function BucketCard({ title, data, testid }) {
  return (
    <section className="card-surface p-5" data-testid={testid}>
      <div className="label-mono mb-3">{title.toUpperCase()}</div>
      <div className="text-4xl h-display tracking-tighter mb-2">{data.total || 0}</div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Row label="Delivered" value={data.sent} color="#CCFF00" icon={CheckCircle2} />
        <Row label="Mocked" value={data.sent_mock} color="#FF4500" icon={AlertTriangle} />
        <Row label="Failed" value={data.failed} color="#EF4444" icon={XCircle} />
        <Row label="Throttled" value={data.throttled} color="#F59E0B" icon={AlertTriangle} />
        <Row label="Queued" value={data.queued} color="#A3A3A3" icon={Mail} />
      </div>
    </section>
  );
}

function Row({ label, value, color, icon: Icon }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <span className="text-[#A3A3A3] uppercase tracking-wider">{label}</span>
      <span className="ml-auto font-mono" style={{ color }}>{value || 0}</span>
    </div>
  );
}
